import { SortModelItem } from "ag-grid-community";
import { downloadZip } from "client-zip";
import exifr from "exifr";
import streamSaver from "streamsaver";

import * as API from "fond/api";
import { basename, extname } from "fond/fileValidation";
import {
  AttachmentActionTypes,
  DELETE_ATTACHMENT_FAILURE,
  DELETE_ATTACHMENT_SUCCESS,
  DELETE_ATTACHMENTS_REQUEST,
  DOWNLOAD_ATTACHMENTS_FAILURE,
  DOWNLOAD_ATTACHMENTS_REQUEST,
  DOWNLOAD_ATTACHMENTS_SUCCESS,
  GET_ATTACHMENTS_FAILURE,
  GET_ATTACHMENTS_REQUEST,
  GET_ATTACHMENTS_SUCCESS,
  UPDATE_ATTACHMENT_FAILURE,
  UPDATE_ATTACHMENT_SUCCESS,
  UPDATE_ATTACHMENTS_REQUEST,
  UPLOAD_ATTACHMENT_ABORT,
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_PROGRESS_UPDATE,
  UPLOAD_ATTACHMENT_REQUEST,
  UPLOAD_ATTACHMENT_SUCCESS,
  UPLOAD_ATTACHMENTS_REQUEST,
} from "fond/redux/attachments";
import {
  AppDispatch,
  AppThunk,
  AppThunkDispatch,
  Attachment,
  AttachmentEntityType,
  AttachmentFilterType,
  AttachmentsResponse,
  BulkFileProps,
  GetAttachmentsParams,
} from "fond/types";

/**
 * GET Attachments
 */

type GetAttachmentsSuccessParams = {
  payload: AttachmentsResponse;
  entityType: AttachmentEntityType;
  entityId: string;
  sortModel?: SortModelItem[];
  filterType?: AttachmentFilterType | null;
  versionIds?: Array<string | null>;
};

const getAttachmentsRequest = (): AttachmentActionTypes.GetAttachmentsRequest => ({ type: GET_ATTACHMENTS_REQUEST });
const getAttachmentsSuccess = ({
  payload,
  entityType,
  entityId,
  sortModel,
  filterType,
  versionIds,
}: GetAttachmentsSuccessParams): AttachmentActionTypes.GetAttachmentsSuccess => ({
  type: GET_ATTACHMENTS_SUCCESS,
  payload,
  entityType,
  entityId,
  sortModel,
  filterType,
  versionIds,
});
const getAttachmentsFailure = (error: Error): AttachmentActionTypes.GetAttachmentsFailure => ({ type: GET_ATTACHMENTS_FAILURE, error });

export const getAttachments =
  ({ projectId, layerFeatureId, pagination, sortModel, filterType, versionIds }: GetAttachmentsParams): AppThunk<Promise<AttachmentsResponse>> =>
  async (dispatch: AppThunkDispatch) => {
    dispatch(getAttachmentsRequest());
    return new Promise<AttachmentsResponse>((resolve, reject) => {
      API.getAttachments({ projectId, layerFeatureId, pagination, sortModel, filterType, versionIds }).then(
        (payload) => {
          dispatch(
            getAttachmentsSuccess({
              payload,
              entityType: layerFeatureId ? "Feature" : "Project",
              entityId: layerFeatureId || projectId,
              sortModel,
              filterType,
              versionIds,
            })
          );
          resolve(payload);
        },
        (error: Error) => {
          dispatch(getAttachmentsFailure(error));
          reject(error);
        }
      );
    });
  };

/**
 * UPDATE Attachments
 */

const updateAttachmentsRequest = (): AttachmentActionTypes.UpdateAttachmentsRequest => ({ type: UPDATE_ATTACHMENTS_REQUEST });
const updateAttachmentSuccess = (attachment: Attachment): AttachmentActionTypes.UpdateAttachmentSuccess => ({
  type: UPDATE_ATTACHMENT_SUCCESS,
  attachment,
});
const updateAttachmentFailure = (error: Error): AttachmentActionTypes.UpdateAttachmentFailure => ({
  type: UPDATE_ATTACHMENT_FAILURE,
  error,
});

export const updateAttachments =
  (attachments: Attachment[]): AppThunk<Promise<void[]>> =>
  async (dispatch: any) => {
    dispatch(updateAttachmentsRequest());

    const updatePromises = attachments.map((attachment: Attachment) => {
      const { Location, Description, ...attachmentWithoutLocation } = attachment;
      return new Promise<void>((resolve, reject) => {
        API.updateAttachment(attachmentWithoutLocation).then(
          () => {
            dispatch(updateAttachmentSuccess(attachment));
            resolve();
          },
          (error: Error) => {
            dispatch(updateAttachmentFailure(error));
            reject(error);
          }
        );
      });
    });

    return Promise.all(updatePromises);
  };

/**
 * DELETE Attachments
 */

const deleteAttachmentsRequest = (): AttachmentActionTypes.DeleteAttachmentsRequest => ({ type: DELETE_ATTACHMENTS_REQUEST });
const deleteAttachmentSuccess = (attachment: Attachment): AttachmentActionTypes.DeleteAttachmentSuccess => ({
  type: DELETE_ATTACHMENT_SUCCESS,
  attachment,
});
const deleteAttachmentFailure = (error: Error): AttachmentActionTypes.DeleteAttachmentFailure => ({
  type: DELETE_ATTACHMENT_FAILURE,
  error,
});

export const deleteAttachments =
  (attachments: Attachment[]): AppThunk<Promise<void>> =>
  async (dispatch: any) => {
    dispatch(deleteAttachmentsRequest());

    const deletePromises = attachments.map((attachment: Attachment) => {
      return new Promise<void>((resolve, reject) => {
        API.deleteAttachment(attachment.ID).then(
          () => {
            dispatch(deleteAttachmentSuccess(attachment));
            resolve();
          },
          (error: Error) => {
            dispatch(deleteAttachmentFailure(error));
            reject(error);
          }
        );
      });
    });

    await Promise.all(deletePromises);
  };

/**
 * UPLOAD Attachments
 */

const uploadAttachmentsRequest = (files: File[]): AttachmentActionTypes.UploadAttachmentsRequest => ({
  type: UPLOAD_ATTACHMENTS_REQUEST,
  files,
});

const uploadAttachmentRequest = (file: File, XHRRequest: XMLHttpRequest): AttachmentActionTypes.UploadAttachmentRequest => ({
  type: UPLOAD_ATTACHMENT_REQUEST,
  file,
  XHRRequest,
});
const uploadAttachmentSuccess = (attachment: Attachment): AttachmentActionTypes.UploadAttachmentSuccess => ({
  type: UPLOAD_ATTACHMENT_SUCCESS,
  attachment,
});
const uploadAttachmentFailure = (file: File, error: Error): AttachmentActionTypes.UploadAttachmentFailure => ({
  type: UPLOAD_ATTACHMENT_FAILURE,
  file,
  error,
});
const uploadAttachmentProgressUpdate = (file: File, percentComplete: number): AttachmentActionTypes.UploadAttachmentProgressUpdate => ({
  type: UPLOAD_ATTACHMENT_PROGRESS_UPDATE,
  percentComplete,
  file,
});
export const uploadAttachmentAbort =
  (file: File): AppThunk =>
  async (dispatch: any, getState: any) => {
    const { uploadStatus } = getState().attachments;
    if (uploadStatus[file.name].progress !== 100) {
      uploadStatus[file.name]?.request?.abort();
      dispatch({ type: UPLOAD_ATTACHMENT_ABORT, file });
    }
  };

/*
 * Upload the files into a directory of s3.
 * The attachment file structure is flat and files must be distinguishable by ID.
 * Any uploads with matching key will be overwritten.
 */
export const uploadAttachments =
  (
    projectId: string,
    files: File[],
    layerFeatureId?: string | null,
    filesWithFeatureDetails?: BulkFileProps[],
    versionId?: string | null
  ): AppThunk<Promise<void>> =>
  async (dispatch: any, getState: any) => {
    dispatch(uploadAttachmentsRequest(files));
    const entityType = layerFeatureId ? "Feature" : "Project";
    const successfullyUploadedAttachmentIds: string[] = [];

    const uploadPromises = files.map(async (file: File) => {
      // Check if the attachment with matching name is already loaded.
      // If it is not then consider this a new file upload rather than an overwrite.

      let attachment = getState().attachments.items[entityType]?.find((item: Attachment) => `${item.Name}.${item.Extension}` === file.name);

      if (attachment != null) {
        // Overwriting an existing attachment — update the version to match the user's selection.
        attachment = { ...attachment, Version: !layerFeatureId && versionId ? { ID: versionId } : null };
      } else {
        const fileExtension = extname(file.name).toLowerCase();
        let exifData;
        if (["png", "jpg", "jpeg"].includes(fileExtension)) {
          exifData = await exifr.gps(file);
        }

        const featureDetails = filesWithFeatureDetails?.find((item: BulkFileProps) => item.file.name === file.name)?.featureDetails;

        try {
          // Create an attachment record. This will provide us with a resource ID to request a presigned post.
          attachment = await API.addAttachment({
            ProjectID: projectId,
            Name: basename(file.name),
            Extension: fileExtension,
            MimeType: file.type || "application/octet-stream", // assume the closest thing to a default type.
            Size: file.size,
            Uploaded: false,
            LayerFeatureID: layerFeatureId,
            Location: {
              latitude: exifData?.latitude,
              longitude: exifData?.longitude,
            },
            FeatureDetails: featureDetails,
            Version: !layerFeatureId && versionId ? { ID: versionId } : null,
          });
        } catch (error) {
          let message: string | null = null;
          if (error instanceof Error && error.message) {
            message = error.message;
          }
          const err = new Error(message ?? `Could not create attachment record.`);
          dispatch(uploadAttachmentFailure(file, err));
          throw err;
        }
      }

      return new Promise<void>((resolve, reject) => {
        if (!attachment) {
          const err = new Error("Attachment not found");
          dispatch(uploadAttachmentFailure(file, err));
          reject(err);
          return;
        }

        // Request a presigned post to authorize the upload of this attachment.
        API.getAttachmentsPresignedPost(attachment.ID).then(
          (presignedPost) => {
            // Check if the upload has been aborted before opening a new upload.
            if (getState().attachments.uploadStatus[file.name]?.isAborted) {
              const error = new Error("Upload Aborted");
              dispatch(uploadAttachmentFailure(file, error));
              reject(error);
              return;
            }

            // The file is renamed for storage using the unique attachment ID.
            const renamedFile = new File([file], `${attachment.ID}.${attachment.Extension}`, { type: file.type });
            // Remove Location from attachment, as update attachment request does not need location info.
            // Location info has been already sent during create attachment request.
            const { Location, Description, ...attachmentWithoutLocation } = attachment;
            // Begin the file upload.
            const XHRRequest = API.uploadFileToS3({
              presignedPost,
              file: renamedFile,
              onProgress: (percentComplete: number) => dispatch(uploadAttachmentProgressUpdate(file, percentComplete)),
              onComplete: () =>
                // Mark the attachment database resource as being uploaded.
                API.updateAttachment({ ...attachmentWithoutLocation, Uploaded: true }).then(
                  (newAttachment) => {
                    dispatch(uploadAttachmentSuccess(newAttachment));
                    const fileExtension = extname(file.name).toLowerCase();
                    if (["png", "jpg", "jpeg"].includes(fileExtension)) {
                      successfullyUploadedAttachmentIds.push(attachment.ID);
                    }
                    resolve();
                  },
                  (error) => {
                    dispatch(uploadAttachmentFailure(file, error));
                    reject(error);
                  }
                ),
              onError: (error: Error) => {
                dispatch(uploadAttachmentFailure(file, new Error("Upload Failed")));
                reject(error);
              },
            });
            dispatch(uploadAttachmentRequest(file, XHRRequest));
          },
          (error) => {
            dispatch(uploadAttachmentFailure(file, error));
            reject(error);
          }
        );
      });
    });

    await Promise.all(uploadPromises);

    // If there are successfully uploaded attachments, generate thumbnails
    if (successfullyUploadedAttachmentIds.length > 0) {
      await dispatch(
        API.projectSlice.endpoints.createAttachmentThumbnails.initiate({
          projectId,
          attachmentIds: successfullyUploadedAttachmentIds,
        })
      );
    }
  };

/**
 * DOWNLOAD Attachments
 */

const downloadAttachmentsRequest = (): AttachmentActionTypes.DownloadAttachmentsRequest => ({ type: DOWNLOAD_ATTACHMENTS_REQUEST });
const downloadAttachmentsSuccess = (): AttachmentActionTypes.DownloadAttachmentsSuccess => ({
  type: DOWNLOAD_ATTACHMENTS_SUCCESS,
});
const downloadAttachmentsFailure = (error: Error): AttachmentActionTypes.DownloadAttachmentsFailure => ({
  type: DOWNLOAD_ATTACHMENTS_FAILURE,
  error,
});

export const downloadAttachments =
  (attachments: Attachment[]): AppThunk<Promise<void>> =>
  async (dispatch: AppDispatch) => {
    dispatch(downloadAttachmentsRequest());

    try {
      const files = [];
      for (const attachment of attachments) {
        const attachmentObject = {
          name: `${attachment.Name}.${attachment.Extension}`,
          input: await fetch(attachment.Urls?.Download || ""),
        };
        files.push(attachmentObject);
      }

      if (files.length === 1) {
        const file = files[0];
        const fileSteam = streamSaver.createWriteStream(file.name);
        const headers: Record<string, string> = { "Content-Disposition": "attachment" };
        const fileResponse = new Response(file.input.body, { headers });
        await fileResponse.body?.pipeTo(fileSteam);
      }

      // Download attachments as a zip file if multiple are selected.
      if (files.length > 1) {
        const fileSteam = streamSaver.createWriteStream("attachments.zip");
        await downloadZip(files).body?.pipeTo(fileSteam);
        dispatch(downloadAttachmentsSuccess());
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        dispatch(downloadAttachmentsFailure(error));
      }
      throw error;
    }
  };
